import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Child content screens: no copy / right-click / print, faint owner watermark, blur when the app loses focus
const GUARDED = /^\/(kid\/|parent\/child\/)/;
const ALWAYS_BLOCK = new Set(["p", "s", "u"]);
const TEXT_BLOCK = new Set(["c", "x", "a"]);
const editable = (el) => el?.closest?.("input, textarea, [contenteditable='true']");
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function watermark(text) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="340" height="190"><text x="20" y="120" transform="rotate(-24 170 95)" font-family="sans-serif" font-size="15" font-weight="700" fill="#0f172a" fill-opacity="0.07">${esc(text)}</text></svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
}

const NOTICE = {
  print: "Printing is turned off · L'impression est désactivée",
  screenshot: "Screenshots are turned off · Les captures sont désactivées",
};
const sent = {};

function report(kind) {
  toast(NOTICE[kind] || "Copying is turned off · La copie est désactivée", { id: "content-guard-notice", duration: 2500 });
  const now = Date.now();
  if (now - (sent[kind] || 0) < 3000) return;
  sent[kind] = now;
  const path = window.location.pathname;
  const child_id = (path.match(/^\/(?:kid|parent\/child)\/([^/]+)/) || [])[1];
  api.post("/guard/attempt", { kind, path: path.slice(0, 200), child_id }).catch(() => {});
}

function useGuardEvents(on, setAway) {
  useEffect(() => {
    if (!on) return undefined;
    const html = document.documentElement;
    html.classList.add("content-guard");
    const KIND = { contextmenu: "right_click", copy: "copy", cut: "copy", dragstart: "drag" };
    const stop = (e) => {
      if (editable(e.target)) return;
      e.preventDefault();
      if (KIND[e.type]) report(KIND[e.type]);
    };
    const keydown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = (e.key || "").toLowerCase();
      if (ALWAYS_BLOCK.has(k) || (TEXT_BLOCK.has(k) && !editable(e.target))) {
        e.preventDefault();
        const kind = { p: "print", s: "save", u: "source", c: "copy", x: "copy" }[k];
        if (kind) report(kind);
      }
    };
    const keyup = (e) => {
      if (e.key !== "PrintScreen") return;
      setAway(true);
      report("screenshot");
      navigator.clipboard?.writeText?.("").catch(() => {});
      setTimeout(() => setAway(false), 1500);
    };
    const beforeprint = () => report("print");
    const blur = () => setTimeout(() => { if (document.activeElement?.tagName !== "IFRAME") setAway(true); }, 0);
    const focus = () => setAway(false);
    const vis = () => setAway(document.hidden);
    const doc = [["contextmenu", stop], ["copy", stop], ["cut", stop], ["dragstart", stop], ["selectstart", stop], ["keydown", keydown], ["keyup", keyup], ["visibilitychange", vis]];
    const win = [["blur", blur], ["focus", focus], ["beforeprint", beforeprint]];
    doc.forEach(([n, f]) => document.addEventListener(n, f));
    win.forEach(([n, f]) => window.addEventListener(n, f));
    return () => {
      html.classList.remove("content-guard");
      doc.forEach(([n, f]) => document.removeEventListener(n, f));
      win.forEach(([n, f]) => window.removeEventListener(n, f));
      setAway(false);
    };
  }, [on, setAway]);
}

export default function ContentGuard() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [away, setAway] = useState(false);
  const on = GUARDED.test(pathname) && !!user && user.role !== "admin";
  useGuardEvents(on, setAway);
  if (!on) return null;
  return (
    <>
      <div aria-hidden className="pointer-events-none fixed inset-0 z-[90]" style={{ backgroundImage: watermark(`${user.email} · Anou Appran`) }} data-testid="content-watermark" />
      {away && (
        <button type="button" onClick={() => setAway(false)} data-testid="content-guard-blur"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-white/50 backdrop-blur-2xl animate-in fade-in duration-150">
          <span className="rounded-full border-[3px] border-slate-900 bg-[#FFDF00] px-6 py-3 font-kids text-lg font-black text-slate-900 shadow-[4px_4px_0_0_#111]">
            Tap to continue · Touche pour continuer
          </span>
        </button>
      )}
    </>
  );
}
